import React, { useState } from 'react';
import { X, CheckIcon, ToggleLeft, ToggleRight } from 'lucide-react';
import Switch from 'react-switch';
import { message } from 'antd';


const categories = ['Plan Sponsor', 'TPA', 'Recordkeeper'];
const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];

const tasks = {
  'Plan Sponsor': {
    'Q1': [
      'Deadline to File 2023 5500 July 31 or request extension. Review and submit as directed by TPA or Recordkeeper.',
      'June 30 Deadline for processing corrective distributions for the ADP/ACP test for certain eligible automatic contribution arrangement plans (does not apply if you are safe harbor or if you successfully pass your ADP/ACP test).',
      'Review Investment Monitoring report 2 funds on watch list (no changes required).',
      'Notify Participants if any investment changes are made.',
      'Confirm investments are performing as expected, costs are reasonable, and appropriate asset classes are available to suit conservative to aggressive risk tolerances',
      'Arrange time for plan health review.',
      'Determine Participant Education date for 2024.',
      'Review eligibility criteria and extend enrollment offer to newly eligible employees if applicable.',
      'Complete annual data request questionnaire and submit annual census data (unless provided by your payroll provider). Log into http://rpsi.us.com. Select Client login on top right. Click begin task for Year End Data Collection under "My Tasks"',
      'Process any required corrections or excess contributions from previous year',
      'Engage Auditor if plan size warrants',
      'Any employer contributions (Profit Sharing, Match etc.) must be made by the due date of your tax return, including extensions.',
    ],
    'Q2': [
      'Deadline to File 2023 5500 July 31 or request extension. Review and submit as directed by TPA or Recordkeeper.',
      'June 30 Deadline for processing corrective distributions for the ADP/ACP test for certain eligible automatic contribution arrangement plans (does not apply if you are safe harbor or if you successfully pass your ADP/ACP test).',
      'Review Investment Monitoring report 2 funds on watch list (no changes required).',
      'Notify Participants if any investment changes are made.',
      'Confirm investments are performing as expected, costs are reasonable, and appropriate asset classes are available to suit conservative to aggressive risk tolerances',
      'Arrange time for plan health review.',
      'Determine Participant Education date for 2024.',
      'Review eligibility criteria and extend enrollment offer to newly eligible employees if applicable.',
      'Review Plan Loan Data to ensure payments are timely',
      'Review Fiduciary Liability Insurance with P&C specialist',
    ],
    'Q3': [
      'Distribution of the summary plan description (only required to new enrollees unless there has been a change in the plan in which case all participants would need to receive it). (Located on RPSI portal).',
      'Confirm Erisa Bond is adequate.',
      'Review Investment Monitoring report 2 funds on watch list (no changes required).',
      'Notify Participants if any investment changes are made.',
      'Confirm investments are performing as expected, costs are reasonable, and appropriate asset classes are available to suit conservative to aggressive risk tolerances',
      'Arrange time for plan health review.',
      'Determine Participant Education date for 2024.',
      'Review eligibility criteria and extend enrollment offer to newly eligible employees if applicable.',
      'Review Service providers and make adjustments if necessary',
      'Review any regulatory changes that require amendments to the plan in the next year (see IRS\'s annual Required Amendments list or consult TPA or RK)',
    ],
    'Q4': [
      'Review Plan design with sponsor to determine if any changes are desired.',
      'ADP/ACP Return of Excess Contributions Authorization due by February 15, 2024, if you failed ADP/ACP. Deadline for returning excess 401(k) contributions to participants without a penalty is March 15, 2024 (2.5 mos. after plan year end).Should be no issue since Safe Harbor.',
      'Review Investment Monitoring report (no changes required).',
      'Notify Participants if any investment changes are made.',
      'Confirm investments are performing as expected, costs are reasonable, and appropriate asset classes are available to suit conservative to aggressive risk tolerances',
      'Arrange time for plan health review.',
      'Determine Participant Education date for 2024.',
      'Review eligibility criteria and extend enrollment offer to newly eligible employees if applicable.',
      'Complete annual data request questionnaire and submit annual census data (unless provided by your payroll provider). Log into http://rpsi.us.com. Select Client login on top right. Click begin task for Year End Data Collection under "My Tasks"',
      'Any employer contributions (Profit Sharing, Match etc.) must be made by the due date of your tax return, including extensions.',
    ],
  },
  'TPA': {
    'Q1': [
      'Request census and testing data.',
      'Assist with Audit if necessary',
    ],
    'Q2': [
      'Prepare 5500 for signature or extension.',
      'Prepare form 8955 ssa (separated participants)',
    ],
    'Q3': [
      'Prepare 5500 for signature or extension.',
      'Prepare form 8955 ssa (separated participants)',
    ],
    'Q4': [
      'Request census and testing data.',
      'Prepare annual required notices (e.g. safe harbor, summary annual report)',
    ],
  },
  'Recordkeeper': {
    'Q1': [
      'Communicate any updates in participant access.',
      'Update notices if necessary (404(a)5 etc)',
    ],
    'Q2': [
      'Prepare 5500 for signature or extension.',
      'Update notices if necessary (404(a)5 etc)',
    ],
    'Q3': [
      'Prepare 5500 for signature or extension.',
      'Update notices if necessary (404(a)5 etc)',
    ],
    'Q4': [
      'Prepare annual required notices (e.g. safe harbor, summary annual report)',
      'Update notices if necessary (404(a)5 etc)',
    ],
  },
};

const RequirementsChecklistSelectionForm = ({ onClose }) => {
  const [activeCategory, setActiveCategory] = useState('Plan Sponsor');
  const [toggleStates, setToggleStates] = useState(
    categories.reduce((acc, category) => ({
      ...acc,
      [category]: quarters.reduce((q, quarter) => ({
        ...q,
        [quarter]: (tasks[category] && tasks[category][quarter]) 
          ? tasks[category][quarter].reduce((t, _, index) => ({ ...t, [index]: true }), {})
          : {},
      }), {}),
    }), {})
  );

  const handleToggle = (category, quarter, taskIndex) => {
    setToggleStates(prevState => ({
      ...prevState,
      [category]: {
        ...prevState[category],
        [quarter]: {
          ...prevState[category][quarter],
          [taskIndex]: !prevState[category][quarter][taskIndex]
        }
      }
    }));
  };

  const handleSaveRequirements = () => {
    // console.log('Saving requirements:', toggleStates);
    message.success('Successfully updated Preset Requirements');
    onClose();
  };

  const toggleAll = (turnOn = true) => {
    setToggleStates(prevState => ({
      ...prevState,
      [activeCategory]: quarters.reduce((q, quarter) => ({
        ...q,
        [quarter]: (tasks[activeCategory] && tasks[activeCategory][quarter])
          ? tasks[activeCategory][quarter].reduce((t, _, index) => ({ ...t, [index]: turnOn }), {})
          : {},
      }), {}),
    }));
  };

  const allOn = Object.values(toggleStates[activeCategory]).every(quarter => 
    Object.values(quarter).every(value => value)
  );

  return (
    <div className="w-full max-w-6xl mx-auto p-6 bg-white rounded-2xl"> {/* Added border classes */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-semibold text-gray-800">401k Requirements Dashboard</h1>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
          <X className="h-6 w-6" />
        </button>
      </div>

      <div className="mb-8 flex flex-wrap justify-between items-center gap-4">
        <div className="flex flex-wrap gap-2">
          {categories.map(category => (
            <button
              key={category}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === category 
                  ? 'bg-blue-500 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => toggleAll(!allOn)}
            className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-600 text-sm font-medium rounded-full hover:bg-gray-200 transition-all"
          >
            {allOn ? (
              <>
                <ToggleLeft className="h-5 w-5 mr-2" />
                Turn All Off
              </>
            ) : (
              <>
                <ToggleRight className="h-5 w-5 mr-2" />
                Turn All On
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {quarters.map(quarter => (
          <div key={quarter} className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">{quarter}</h2>
            {tasks[activeCategory] && tasks[activeCategory][quarter] ? (
              tasks[activeCategory][quarter].map((task, index) => (
                <div 
                  key={index} 
                  className="p-4 rounded-lg bg-gray-50 flex items-start justify-between transition-all border border-gray-200 shadow-sm hover:shadow-lg"
                >
                  <p className="text-sm text-gray-700 flex-grow mr-4">{task}</p>
                  <Switch
                    checked={toggleStates[activeCategory][quarter][index] || false}
                    onChange={() => handleToggle(activeCategory, quarter, index)}
                    onColor="#3b82f6"
                    offColor="#d1d5db"
                    checkedIcon={false}
                    uncheckedIcon={false}
                    height={24}
                    width={48}
                    handleDiameter={20}
                    boxShadow="0 0 0 2px white"
                    activeBoxShadow="0 0 0 2px white"
                  />
                </div>
              ))
            ) : (
              <p className="text-gray-500 italic text-sm">No tasks for this quarter.</p>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 flex justify-end">
        <button
          onClick={handleSaveRequirements}
          className="inline-flex items-center px-6 py-3 bg-blue-500 text-white text-sm font-medium rounded-full shadow-md hover:bg-blue-600 transition-all"
        >
          <CheckIcon className="h-5 w-5 mr-2" />
          Save Requirements
        </button>
      </div>
    </div>
  );
};

export default RequirementsChecklistSelectionForm;